/**
 * The design system's 12 empty-state illustrations (assets/Illustrations in the JobCrawls Design
 * System), inlined so they need no public asset path. They are stickers: the same colours in every
 * theme by design, which is why this folder is exempt from the token-discipline test. Decorative:
 * the component renders them aria-hidden.
 */
import type { ReactNode } from 'react';

export const ILLUSTRATION_NAMES = [
  'welcome',
  'empty-search',
  'no-results',
  'empty-bookmarks',
  'empty-inbox',
  'all-caught-up',
  'binoculars',
  'insights',
  'lost-route',
  'map-pin',
  'offline',
  'web-page',
] as const;
export type IllustrationName = (typeof ILLUSTRATION_NAMES)[number];

const ART: Record<IllustrationName, ReactNode> = {
  welcome: (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <path d="M36 78a24 24 0 0 1 48 0z" fill="#FFFFFF" stroke="#17181B" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M22 78h76" stroke="#17181B" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M30 90h60M44 100h32" stroke="#2F5A43" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="60" y1="34" x2="60" y2="42" stroke="#2F5A43" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="34" y1="46" x2="40" y2="52" stroke="#2F5A43" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="86" y1="46" x2="80" y2="52" stroke="#2F5A43" strokeWidth="2.5" strokeLinecap="round" />
    </>
  ),
  'empty-search': (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <line x1="74" y1="74" x2="94" y2="94" stroke="#2F5A43" strokeWidth="8" strokeLinecap="round" />
      <circle cx="54" cy="54" r="26" fill="#FFFFFF" stroke="#17181B" strokeWidth="2.5" />
      <line x1="42" y1="48" x2="66" y2="48" stroke="#E7E7E2" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="42" y1="58" x2="58" y2="58" stroke="#E7E7E2" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="92" cy="28" r="3.5" fill="#2F5A43" />
      <circle cx="24" cy="86" r="2.5" fill="#2F5A43" />
    </>
  ),
  'no-results': (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <rect
        x="30"
        y="26"
        width="54"
        height="68"
        rx="8"
        fill="#FFFFFF"
        stroke="#17181B"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <line x1="40" y1="42" x2="64" y2="42" stroke="#E7E7E2" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="40" y1="53" x2="74" y2="53" stroke="#E7E7E2" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="40" y1="64" x2="56" y2="64" stroke="#E7E7E2" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="84" cy="84" r="15" fill="#2F5A43" stroke="#FFFFFF" strokeWidth="2.5" />
      <line x1="78" y1="78" x2="90" y2="90" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
      <line x1="90" y1="78" x2="78" y2="90" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
      <circle cx="94" cy="34" r="3" fill="#2F5A43" />
    </>
  ),
  'empty-bookmarks': (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <path
        d="M30 30h30a4 4 0 0 1 4 4v52l-19-12-19 12V34a4 4 0 0 1 4-4z"
        fill="#E2ECE4"
        stroke="#17181B"
        strokeWidth="2.5"
        strokeLinejoin="round"
        transform="rotate(-8 45 60)"
      />
      <path
        d="M48 24h32a4 4 0 0 1 4 4v62l-20-13-20 13V28a4 4 0 0 1 4-4z"
        fill="#FFFFFF"
        stroke="#17181B"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <line x1="64" y1="43" x2="64" y2="59" stroke="#2F5A43" strokeWidth="3" strokeLinecap="round" />
      <line x1="56" y1="51" x2="72" y2="51" stroke="#2F5A43" strokeWidth="3" strokeLinecap="round" />
      <line x1="96" y1="30" x2="96" y2="38" stroke="#2F5A43" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="92" y1="34" x2="100" y2="34" stroke="#2F5A43" strokeWidth="2.5" strokeLinecap="round" />
    </>
  ),
  'empty-inbox': (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <path
        d="M24 66l10-24a4 4 0 0 1 4-3h44a4 4 0 0 1 4 3l10 24v20a6 6 0 0 1-6 6H30a6 6 0 0 1-6-6z"
        fill="#FFFFFF"
        stroke="#17181B"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path
        d="M24 66h22a14 14 0 0 0 28 0h22"
        fill="none"
        stroke="#17181B"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx="60" cy="26" r="5" fill="#2F5A43" />
      <circle cx="84" cy="24" r="3" fill="#2F5A43" />
      <circle cx="36" cy="30" r="2.5" fill="#2F5A43" />
    </>
  ),
  'all-caught-up': (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <circle cx="60" cy="62" r="27" fill="#FFFFFF" stroke="#17181B" strokeWidth="2.5" />
      <path
        d="M48 63l9 9 16-18"
        fill="none"
        stroke="#2F5A43"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line x1="60" y1="18" x2="60" y2="26" stroke="#2F5A43" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="26" y1="36" x2="32" y2="42" stroke="#2F5A43" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="94" y1="36" x2="88" y2="42" stroke="#2F5A43" strokeWidth="2.5" strokeLinecap="round" />
    </>
  ),
  binoculars: (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <rect x="50" y="36" width="20" height="26" rx="5" fill="#FFFFFF" stroke="#17181B" strokeWidth="2.5" />
      <rect x="28" y="50" width="26" height="42" rx="10" fill="#FFFFFF" stroke="#17181B" strokeWidth="2.5" />
      <rect x="66" y="50" width="26" height="42" rx="10" fill="#FFFFFF" stroke="#17181B" strokeWidth="2.5" />
      <circle cx="41" cy="82" r="7" fill="#2F5A43" />
      <circle cx="79" cy="82" r="7" fill="#2F5A43" />
      <circle cx="94" cy="30" r="3" fill="#2F5A43" />
      <circle cx="24" cy="36" r="2.5" fill="#2F5A43" />
    </>
  ),
  insights: (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <rect x="30" y="62" width="16" height="28" rx="4" fill="#FFFFFF" stroke="#17181B" strokeWidth="2.5" />
      <rect x="52" y="44" width="16" height="46" rx="4" fill="#2F5A43" stroke="#17181B" strokeWidth="2.5" />
      <rect x="74" y="54" width="16" height="36" rx="4" fill="#FFFFFF" stroke="#17181B" strokeWidth="2.5" />
      <line x1="24" y1="92" x2="96" y2="92" stroke="#17181B" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="82" cy="32" r="6" fill="#2F5A43" />
    </>
  ),
  'lost-route': (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <circle cx="60" cy="62" r="32" fill="#FFFFFF" stroke="#17181B" strokeWidth="2.5" />
      <line x1="60" y1="34" x2="60" y2="40" stroke="#17181B" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="60" y1="84" x2="60" y2="90" stroke="#17181B" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="32" y1="62" x2="38" y2="62" stroke="#17181B" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="82" y1="62" x2="88" y2="62" stroke="#17181B" strokeWidth="2.5" strokeLinecap="round" />
      <path
        d="M72 50 L56 58 L48 74 L64 66 Z"
        fill="#2F5A43"
        stroke="#17181B"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <circle cx="60" cy="62" r="3" fill="#17181B" />
      <circle cx="94" cy="30" r="3" fill="#2F5A43" />
      <circle cx="26" cy="92" r="2.5" fill="#2F5A43" />
    </>
  ),
  'map-pin': (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <path
        d="M60 98C46 82 38 72 38 58a22 22 0 0 1 44 0c0 14-8 24-22 40z"
        fill="#2F5A43"
        stroke="#17181B"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <circle cx="60" cy="58" r="8" fill="#FFFFFF" stroke="#17181B" strokeWidth="2.5" />
      <circle cx="92" cy="30" r="3" fill="#2F5A43" />
      <circle cx="26" cy="40" r="2.5" fill="#2F5A43" />
    </>
  ),
  offline: (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <path
        d="M38 84a16 16 0 0 1-2-31.8A22 22 0 0 1 78 46a18 18 0 0 1 4 38z"
        fill="#FFFFFF"
        stroke="#17181B"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <circle cx="84" cy="84" r="14" fill="#2F5A43" stroke="#FFFFFF" strokeWidth="2.5" />
      <line x1="84" y1="77" x2="84" y2="85" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
      <circle cx="84" cy="90" r="1.6" fill="#FFFFFF" />
      <circle cx="26" cy="36" r="3" fill="#2F5A43" />
      <circle cx="96" cy="30" r="2.5" fill="#2F5A43" />
    </>
  ),
  'web-page': (
    <>
      <circle cx="60" cy="62" r="46" fill="#FFAD1F" />
      <rect
        x="30"
        y="24"
        width="56"
        height="72"
        rx="8"
        fill="#FFFFFF"
        stroke="#17181B"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <rect x="39" y="34" width="24" height="8" rx="3" fill="#2F5A43" />
      <line x1="39" y1="54" x2="77" y2="54" stroke="#E7E7E2" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="39" y1="64" x2="72" y2="64" stroke="#E7E7E2" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="39" y1="74" x2="60" y2="74" stroke="#E7E7E2" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="86" cy="84" r="15" fill="#2F5A43" stroke="#FFFFFF" strokeWidth="2.5" />
      <path
        d="M80 90 L92 78 M84 78 H92 V86"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="96" cy="32" r="3" fill="#2F5A43" />
    </>
  ),
};

const SIZE = { sm: 'size-20', md: 'size-28', lg: 'size-36' } as const;

export function Illustration({
  name,
  size = 'md',
  className,
}: {
  name: IllustrationName;
  size?: keyof typeof SIZE;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 120 120"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={[SIZE[size], className].filter(Boolean).join(' ')}
    >
      {ART[name]}
    </svg>
  );
}
